import { Fragment, useState } from 'react';
import { Dialog, Transition, Combobox } from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import {
  ChartBarIcon,
  ChartPieIcon,
  ChatBubbleLeftRightIcon,
  CogIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import { useAdminUI } from '@/stores';
import { cn } from '@/utils/cn';

interface SearchResult {
  id: string;
  title: string;
  description?: string;
  icon: React.ComponentType<{ className?: string }>;
  action: () => void;
}

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SearchModal = ({ isOpen, onClose }: SearchModalProps) => {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const adminUI = useAdminUI();

  // Navigation items (Data Explorer and AI Chat exist only in the admin app)
  const navigationItems: SearchResult[] = [
    {
      id: 'trading',
      title: 'Trading',
      description: 'Spot and futures trading',
      icon: ChartBarIcon,
      action: () => {
        navigate(ROUTES.TRADING.ROOT);
        onClose();
      },
    },
    ...(adminUI
      ? [
          {
            id: 'data-explorer',
            title: 'Data Explorer',
            description: 'Market data and analytics',
            icon: ChartPieIcon,
            action: () => {
              navigate(ROUTES.DATA_EXPLORER.ROOT);
              onClose();
            },
          },
          {
            id: 'ai-chat',
            title: 'AI Chat',
            description: 'Trading assistant',
            icon: ChatBubbleLeftRightIcon,
            action: () => {
              navigate(ROUTES.AI_CHAT.ROOT);
              onClose();
            },
          },
        ]
      : []),
    {
      id: 'settings',
      title: 'Settings',
      description: 'Preferences and configuration',
      icon: CogIcon,
      action: () => {
        navigate(ROUTES.SETTINGS);
        onClose();
      },
    },
  ];

  const filteredItems =
    query === ''
      ? navigationItems
      : navigationItems.filter((item) =>
          item.title.toLowerCase().includes(query.toLowerCase())
        );

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 pt-[20vh]">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-xl border bg-white shadow-2xl transition-all dark:border-gray-800 dark:bg-gray-900">
                <Combobox
                  onChange={(item: SearchResult) => {
                    item.action();
                  }}
                >
                  {/* Search input */}
                  <div className="relative flex items-center border-b dark:border-gray-800">
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-3 h-5 w-5 text-gray-400" />
                    <Combobox.Input
                      className="h-12 w-full border-0 bg-transparent pl-10 pr-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-0 dark:text-gray-100"
                      placeholder="Search..."
                      onChange={(event) => setQuery(event.target.value)}
                      autoFocus
                    />
                    <kbd className="mr-3 hidden rounded border border-gray-700 bg-gray-800 px-1.5 py-0.5 text-xs text-gray-400 sm:block">
                      ESC
                    </kbd>
                  </div>

                  {/* Results */}
                  {filteredItems.length > 0 && (
                    <Combobox.Options
                      static
                      className="max-h-96 scroll-py-1 overflow-y-auto p-2"
                    >
                      {filteredItems.map((item) => (
                        <Combobox.Option
                          key={item.id}
                          value={item}
                          className={({ active }) =>
                            cn(
                              'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                              active
                                ? 'bg-gray-800 dark:bg-gray-800'
                                : 'text-gray-900 dark:text-gray-100'
                            )
                          }
                        >
                          {({ active }) => (
                            <>
                              <div
                                className={cn(
                                  'flex h-8 w-8 items-center justify-center rounded-md',
                                  active
                                    ? 'bg-gray-700 text-gray-200'
                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                )}
                              >
                                <item.icon className="h-4 w-4" />
                              </div>
                              <div className="flex-1">
                                <div className="text-sm font-medium">{item.title}</div>
                                {item.description && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {item.description}
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </Combobox.Option>
                      ))}
                    </Combobox.Options>
                  )}

                  {/* No results */}
                  {query !== '' && filteredItems.length === 0 && (
                    <div className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                      No results found for "{query}"
                    </div>
                  )}
                </Combobox>

                {/* Footer */}
                <div className="flex items-center justify-between border-t bg-gray-50 px-3 py-2 text-xs text-gray-500 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-400">
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1">
                      <kbd className="rounded border bg-gray-800 px-1.5 py-0.5 dark:border-gray-700">
                        ↑↓
                      </kbd>
                      Navigate
                    </span>
                    <span className="flex items-center gap-1">
                      <kbd className="rounded border bg-gray-800 px-1.5 py-0.5 dark:border-gray-700">
                        ⏎
                      </kbd>
                      Select
                    </span>
                  </div>
                  <span>⌘K to open</span>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
